import { Injectable, inject, computed } from '@angular/core';
import { Brand } from '../models/brand.enum';
import { ThemeService } from './theme.service';

interface Translations {
  brandName: string;
  mijnText: string;
  mijnUrl: string;
  mijnDomain: string;
  emailPreferencesUrl: string;
  homeUrl: string;
  loyaltyProgram: string;
  loyaltyProgramUrl: string;
  customerServiceUrl: string;
  logoPathTop: string;
  logoPathBottom: string;
  faviconPath: string;
  pageTitle: string;
  mainHeading: string;
  successTitle: string;
  successStillReceiving: string;
  successOptedOut: string;
  successFooter: string;
  successButton: string;
  saveButton: string;
  savingButton: string;
  errorMessage: string;
  tokenExpired: string;
  tokenValidationFailed: string;
  noTokenFound: string;
  googlePlayUrl: string;
  appStoreUrl: string;
}

const shared: Pick<
  Translations,
  | 'mainHeading'
  | 'successTitle'
  | 'successStillReceiving'
  | 'successOptedOut'
  | 'successButton'
  | 'saveButton'
  | 'savingButton'
  | 'errorMessage'
  | 'tokenExpired'
  | 'tokenValidationFailed'
  | 'noTokenFound'
> = {
  mainHeading: 'Hoe mogen we je op de hoogte houden?',
  successTitle: 'Je voorkeuren zijn opgeslagen',
  successStillReceiving: 'Je blijft deze mails ontvangen:',
  successOptedOut: 'Je hebt je uitgeschreven voor:',
  successButton: 'Voorkeuren aanpassen',
  saveButton: 'Voorkeuren opslaan',
  savingButton: 'Opslaan...',
  errorMessage: 'Er is een fout opgetreden bij het opslaan van je voorkeuren. Probeer het opnieuw.',
  tokenExpired:
    'Helaas de link is verlopen. Mocht je je voorkeuren willen aanpassen dan kan dat via',
  tokenValidationFailed:
    'Helaas er gaat iets mis. Mocht je je voorkeuren willen aanpassen dan kan dat via',
  noTokenFound: 'Helaas er gaat iets mis. Mocht je je voorkeuren willen aanpassen dan kan dat via',
};

const essentBase = {
  mijnUrl: 'https://mijn.essent.nl/',
  mijnDomain: 'mijn.essent.nl',
  emailPreferencesUrl: 'https://mijn.essent.nl/privacyinstellingen/e-mailvoorkeuren',
  homeUrl: 'https://www.essent.nl/',
  customerServiceUrl: 'https://www.essent.nl/klantenservice',
  logoPathTop: '/assets/essent_logo.svg',
  logoPathBottom: '/assets/essent_logo_white.svg',
  faviconPath: '/assets/favicon-essent.ico',
  googlePlayUrl: 'https://play.google.com/store/apps/details?id=nl.essent.selfservice',
  appStoreUrl: 'https://apps.apple.com/nl/app/essent/id1449710540',
} as const;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly translations: Record<Brand, Translations> = {
    [Brand.ESSENT]: {
      ...shared,
      ...essentBase,
      brandName: 'Essent',
      mijnText: 'Mijn Essent',
      loyaltyProgram: 'Thuisvoordeel',
      loyaltyProgramUrl: 'https://www.essent.nl/thuisvoordeel',
      pageTitle: 'Emailvoorkeuren - Essent',
      successFooter:
        'Mocht je je voorkeuren willen aanpassen dan kan dat via onderstaande knop of via mijn.essent.nl',
    },
    [Brand.ENERGIEDIRECT]: {
      ...shared,
      brandName: 'EnergieDirect',
      mijnText: 'Mijn EnergieDirect',
      mijnUrl: 'https://mijn.energiedirect.nl/',
      mijnDomain: 'mijn.energiedirect.nl',
      emailPreferencesUrl: 'https://mijn.energiedirect.nl/privacyinstellingen/e-mailvoorkeuren',
      homeUrl: 'https://www.energiedirect.nl/',
      loyaltyProgram: 'Klantprogramma',
      loyaltyProgramUrl: 'https://www.energiedirect.nl/klanten/goedzitters',
      customerServiceUrl: 'https://www.energiedirect.nl/klantenservice',
      logoPathTop: '/assets/energiedirect_logo_top.png',
      logoPathBottom: '/assets/energiedirect_logo_bottom.png',
      faviconPath: '/assets/favicon-energiedirect.ico',
      pageTitle: 'Emailvoorkeuren - EnergieDirect',
      successFooter:
        'Mocht je je voorkeuren willen aanpassen dan kan dat via onderstaande knop of via mijn.energiedirect.nl',
      googlePlayUrl:
        'https://play.google.com/store/apps/details?id=nl.energiedirect.selfserviceapp',
      appStoreUrl: 'https://apps.apple.com/nl/app/regelneef-energiedirect/id1176517302',
    },
    [Brand.SME]: {
      ...shared,
      ...essentBase,
      brandName: 'Essent Zakelijk',
      mijnText: 'Mijn Essent Zakelijk',
      loyaltyProgram: 'Zaakvoordeel',
      loyaltyProgramUrl: 'https://www.essent.nl/zaakvoordeel',
      pageTitle: 'Emailvoorkeuren - Essent Zakelijk',
      successFooter:
        'Mocht je je voorkeuren willen aanpassen dan kan dat via onderstaande knop of via mijn.essent.nl',
    },
  };

  readonly #themeService = inject(ThemeService);

  readonly currentBrand = computed(() => this.#themeService.brand() || Brand.ESSENT);

  readonly t = computed(() => this.translations[this.currentBrand()]);
}
